import { useState, useEffect } from 'react';

import Header from '@/components/ui/header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserForm } from '@/components/users/UserForm';
import { serviceGetAllUsers, serviceDeleteUser, serviceCreateUser } from '@/services/user';
import { User } from '@/types';
import { DeleteUser } from '@/components/users/DeleteUser';
import { toast } from '@/components/ui/use-toast';
import Footer from '@/components/ui/footer';

const UserPage = () => {
  const [userList, setUserList] = useState<User[]>([]);
  const [editUser, setEditUser] = useState<User | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const callApi = async () => {
    const result = await serviceGetAllUsers();
    setUserList(result);
  };

  useEffect(() => {
    callApi();
  }, []);

  const handleCreateOrUpdate = async () => {
    if (!name.trim() || !email.trim() || !password.trim()) return;

    const user = { name, email, password };
    try {
      await serviceCreateUser(user).then(() => {
        toast({
          title: 'Usuário cadastrado com sucesso!',
          variant: 'default',
        });
        callApi();
        handleClear();
      });
    } catch (error) {
      toast({
        title: 'Erro ao cadastrar o usuário.',
        description: error?.message || 'Ocorreu um erro ao cadastrar o usuário. Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await serviceDeleteUser(id).then(() => {
        toast({
          title: 'Usuário excluído com sucesso!',
          variant: 'default',
        });

        callApi();
      });
    } catch (error) {
      toast({
        title: 'Erro ao excluir o usuário.',
        description: error?.message || 'Ocorreu um erro ao excluir o usuário. Tente novamente.',
        variant: 'destructive',
      });
    }
  };

  const handleClear = () => {
    setEditUser(null);
    setName('');
    setEmail('');
    setPassword('');
  };

  const disabled = !name.trim() || !email.trim() || !password.trim();

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary to-background">
      <Header />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-5xl mx-auto space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{editUser ? 'Editar Usuário' : 'Cadastrar Usuário'}</CardTitle>
            </CardHeader>
            <CardContent>
              <UserForm
                name={name}
                email={email}
                password={password}
                onNameChange={setName}
                onEmailChange={setEmail}
                onPasswordChange={setPassword}
                onSubmit={handleCreateOrUpdate}
                isEdit={!!editUser}
                setEditUser={() => handleClear()}
                disabled={disabled}
              />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>Usuários cadastrados</CardTitle>
            </CardHeader>
            <CardContent>
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="text-left py-2 px-4">Nome</th>
                    <th className="text-left py-2 px-4">Email</th>
                    <th className="py-2 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {userList.map((u) => (
                    <tr key={u.id} className="border-b last:border-b-0">
                      <td className="py-2 px-4">{u.name}</td>
                      <td className="py-2 px-4">{u.email}</td>
                      <td className="flex gap-2 py-2 px-4">
                        {!u.isAdmin && <DeleteUser handleDelete={() => handleDelete(u.id)} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UserPage;
